import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useUnperformedExamsCount, useUnperformedExamsData, useDeleteExam, invalidateAllWidgetQueries } from '../../../hooks/useWidgetData';
import { toast } from '@rickylandino/react-messages';
import { CheckCircle, Trash2 } from 'lucide-react';
import { Table, Modal } from 'antd';
import Globals from '../../../config/globals';
import PatientExamPane from '../../Exams/PatientExamPane';
import { calculateAge } from '../../Helpers/PatientHelper';
import { getDeletionData, getPatientExamResults } from '../../../common/services/ExamService';

/**
 * UnperformedExamsWidget component
 * Shows all unperformed exams with server-side pagination
 * @param {Object} props
 * @param {boolean} [props.isCollapsed=false] - Whether the widget is collapsed
 * @param {boolean} [props.isExpanded=false] - Whether the widget is in fullscreen mode
 * @param {Function} [props.setIsLoading] - Callback to set loading state for parent
 * @param {Function} [props.setSharedState] - Callback to update shared state (including refetch)
 */
export default function UnperformedExamsWidget({ isCollapsed = false, isExpanded = false, setIsLoading, setSharedState, queryEnabled = true }) {
// Get query client for cache invalidation
const queryClient = useQueryClient();
    
// Delete exam mutation
const deleteExamMutation = useDeleteExam();

// Pagination state
const [currentPage, setCurrentPage] = useState(1);
const [pageSize, setPageSize] = useState(50);
    
// Exam pane state
const [showAddExamPane, setShowAddExamPane] = useState(false);
const [selectedPatient, setSelectedPatient] = useState(null);
const [selectedPatientExam, setSelectedPatientExam] = useState(null);
const [examResults, setExamResults] = useState(null);
const [sharedFollowUpIDs, setSharedFollowUpIDs] = useState(null);
const [facility, setFacility] = useState(null);
    
    // Deletion modal state
    const [showDeletionModal, setShowDeletionModal] = useState(false);
    const [deletionReasons, setDeletionReasons] = useState([]);
    const [deletionReason, setDeletionReason] = useState('');
    const [examToDelete, setExamToDelete] = useState(null);
    const [shouldDeleteGroupedExam, setShouldDeleteGroupedExam] = useState(false);
    const [groupedExamType, setGroupedExamType] = useState('');

    // Determine when to load full data: either expanded in card OR in fullscreen mode
    const shouldLoadFullData = !isCollapsed || isExpanded;

    // Fetch count when collapsed AND not expanded
    const { data: countData, isLoading: isLoadingCount } = useUnperformedExamsCount((isCollapsed && !isExpanded) && queryEnabled);

    // Fetch full data when expanded in card or fullscreen
    const { 
        data, 
        isLoading, 
        isFetching,
        isError, 
        error,
        refetch
    } = useUnperformedExamsData(currentPage, pageSize, shouldLoadFullData);

    const results = data?.data || [];
    const totalCount = data?.totalCount || 0;
    const count = shouldLoadFullData ? totalCount : (countData?.count || 0);

    // Notify parent of loading state for fullscreen overlay
    useEffect(() => {
        if (setIsLoading && isExpanded) {
            setIsLoading(isLoading || isFetching);
        }
    }, [isLoading, isFetching, isExpanded, setIsLoading]);

    // Show error toast when full data fails to load
    useEffect(() => {
        if (shouldLoadFullData && isError) {
            console.error('Failed to load unperformed exams:', error);
            toast.error(`Failed to load unperformed exams: ${error?.message || 'Unknown error'}`);
        }
    }, [shouldLoadFullData, isError, error]);

    // Set refetch function in shared state when it becomes available
    useEffect(() => {
        if (setSharedState && refetch) {
            setSharedState(prev => ({ ...prev, refetch }));
        }
    }, [setSharedState, refetch]);

    /**
     * Format MRN with spaces every 10 characters - memoized
     */
    const formatMRN = useCallback((mrn) => {
        if (!mrn) return '';
        return mrn.replace(/(.{10})/g, '$1 ').trim();
    }, []);

    /**
     * Format date to short format - memoized
     */
    const formatDate = useCallback((dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            month: 'numeric', 
            day: 'numeric', 
            year: 'numeric' 
        });
    }, []);

    /**
     * Sorted results - earliest scheduled exams first
     */
    const sortedResults = useMemo(() => {
        if (!results || results.length === 0) return [];
        
        return [...results].sort((a, b) => {
            const aDate = new Date(a.examDateTime).getTime();
            const bDate = new Date(b.examDateTime).getTime();
            return aDate - bDate;
        });
    }, [results]);

    /**
     * Handle performing an exam - opens PatientExamPane
     */
    const handlePerformExam = useCallback(async (row) => {
        try {
            // Get user info from session storage
            const userInfo = JSON.parse(window.sessionStorage.getItem("userInfo"));
            
            if (!userInfo?.userId) {
                toast.error('User information not found. Please log in again.');
                return;
            }

            // Validate required fields
            if (!row.patientExamID || !row.facilityID) {
                toast.error('Missing required exam information');
                console.error('Missing fields:', { row });
                return;
            }


            // Prepare request payload
            const postdata = {
                uniqueID: row.patientExamID,
                facilityID: row.facilityID,
                name: row.examClass,
                userID: userInfo.userId
            };

            // Fetch exam results
            const data = await getPatientExamResults(postdata);

            if (!data) {
                throw new Error('No data returned from server');
            }

            let patient = {
                dob: row.patientDob || row.dob,
                patientName: row.patientName,
                mrn: row.mrn
            }

            // Set state to show the exam pane
            setSelectedPatient(patient);
            setSelectedPatientExam(data.examInfo);
            setExamResults(data.resultInfo);
            setSharedFollowUpIDs(data.examIDs);
            setFacility({
                facilityID: row.facilityID,
                facilityName: row.facilityName
            });
            setShowAddExamPane(true);

        } catch (error) {
            console.error('Error loading exam:', error);
            toast.error(`Failed to load exam: ${error?.message || 'Unknown error'}`);
        }
    }, []);

    /**
     * Handle deleting an exam - loads deletion reasons and shows modal
     */
    const handleDeleteExam = useCallback(async (row) => {
        try {
            // Load deletion reasons
            const reasons = await getDeletionData(row.groupedExam);

            
            setDeletionReasons(reasons?.reasonForDeletion || []);
            setGroupedExamType(reasons?.groupedExamType || "");

            setExamToDelete(row);
            setShowDeletionModal(true);
            
        } catch (error) {
            console.error('Error loading deletion reasons:', error);
            toast.error('Failed to load deletion reasons');
        }
    }, []);

    /**
     * Close deletion modal and reset state
     */
    const closeDeletionModal = useCallback(() => {
        setShowDeletionModal(false);
        setDeletionReason('');
        setExamToDelete(null);
        setShouldDeleteGroupedExam(false);
    }, []);

    /**
     * Handle the actual deletion after reason is selected
     */
    const handleConfirmDelete = useCallback(async () => {
        if (!deletionReason) {
            toast.warning('Please select a reason for deletion');
            return;
        }

        try {
            // Validate required fields
            if (!examToDelete?.patientExamID) {
                toast.error('Missing exam ID');
                return;
            }

            // Call the delete mutation
            await deleteExamMutation.mutateAsync({
                examId: examToDelete.patientExamID,
                reasonForDeletion: deletionReason,
                deleteBothGroupedExams: shouldDeleteGroupedExam
            });

            // Close modal
            closeDeletionModal();
            
            // Show success message
            toast.success('Exam deleted successfully');

        } catch (error) {
            console.error('Error deleting exam:', error);
            toast.error(`Failed to delete exam: ${error?.response?.data?.message || error?.message || 'Unknown error'}`);
        }
    }, [deletionReason, examToDelete, shouldDeleteGroupedExam, deleteExamMutation, closeDeletionModal]);

    /**
     * Update exam info after changes
     */
    const updateExamInfo = useCallback(() => {
        toast.destroy('loading');
        toast.destroy("updating");

        setShowAddExamPane(false);

        // Invalidate all widget queries since exam changes can affect multiple widgets
        invalidateAllWidgetQueries(queryClient);

        toast.success('Widget data refreshed');
    }, [queryClient]);

    /**
     * Get new exam info and refresh widget
     */
    const getNewExamInfo = useCallback(() => {
        toast.destroy('loading');
        toast.destroy("updating");

        setShowAddExamPane(false);

        // Invalidate all widget queries since exam changes can affect multiple widgets
        invalidateAllWidgetQueries(queryClient);
        
        toast.success('Widget data refreshed');
    }, [queryClient]);

    /**
     * Table columns configuration - memoized to prevent recreation
     */
    const columns = useMemo(() => [
        {
            title: 'Patient Name',
            dataIndex: 'patientName',
            key: 'patientName',
            width: 200,
        },
        {
            title: 'MRN',
            dataIndex: 'mrn',
            key: 'mrn',
            render: (mrn) => <span style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>{formatMRN(mrn)}</span>,
            width: 150,
        },
        {
            title: 'Facility',
            dataIndex: 'facilityName',
            key: 'facilityName',
            width: 180,
        },
        {
            title: 'Scheduled Date',
            dataIndex: 'examDateTime',
            key: 'examDateTime',
            render: (examDateTime) => formatDate(examDateTime),
            width: 140,
        },
        {
            title: 'Exam Type',
            dataIndex: 'examType',
            key: 'examType',
            width: 150,
        },
        {
            title: 'Actions',
            key: 'action',
            width: 140,
            fixed: 'right',
            render: (_, row) => (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                        onClick={(e) => {
                            e.stopPropagation(); // Prevent row click
                            handlePerformExam(row);
                        }}
                        className="px-3 py-1.5 rounded-lg font-medium text-sm transition-colors"
                        style={{
                            backgroundColor: Globals.theme === 'dark' ? '#ec4899' : '#db2777',
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 
                                Globals.theme === 'dark' ? '#db2777' : '#be185d';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 
                                Globals.theme === 'dark' ? '#ec4899' : '#db2777';
                        }}
                    >
                        Perform
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation(); // Prevent row click
                            handleDeleteExam(row);
                        }}
                        className="p-1.5 rounded-lg transition-colors"
                        style={{
                            backgroundColor: 'transparent',
                            color: Globals.theme === 'dark' ? '#f87171' : '#dc2626',
                            border: `1px solid ${Globals.theme === 'dark' ? '#f87171' : '#dc2626'}`,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 
                                Globals.theme === 'dark' ? '#7f1d1d' : '#fee2e2';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        title="Delete exam"
                    >
                        <Trash2 size={16} />
                    </button>
                </div>
            )
        }
    ], [formatMRN, formatDate, handlePerformExam, handleDeleteExam]);

    /**
     * Get count color based on theme
     */
    const getCountColor = () => {
        return Globals.theme === 'dark' ? '#ec4899' : '#db2777';
    };

    /**
     * Get count message
     */
    const getCountMessage = () => {
        return count === 1 ? 'unperformed exam' : 'unperformed exams';
    };

    // COLLAPSED STATE - Show only count
    if (isCollapsed && !isExpanded) {
        if (isLoadingCount) {
            return (
                <div className="flex items-center justify-center py-4">
                    <div 
                        className="animate-spin rounded-full h-8 w-8 border-b-2"
                        style={{ borderColor: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}
                    />
                </div>
            );
        }

        return (
            <div className="flex flex-col items-center justify-center py-4">
                <div 
                    className="text-5xl font-light mb-2"
                    style={{ color: getCountColor() }}
                >
                    {count.toLocaleString()}
                </div>
                <div 
                    className="text-sm"
                    style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}
                >
                    {getCountMessage()}
                </div>
            </div>
        );
    }

    // Success - no unperformed exams
    if (!isLoading && count === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-4">
                <CheckCircle 
                    className="h-16 w-16 mx-auto mb-4"
                    style={{ color: getCountColor() }}
                />
                <p 
                    style={{ color: getCountColor() }}
                >
                    No Unperformed Exams Found
                </p>
            </div>
        );
    }

    // EXPANDED STATE - Show table with server-side pagination
    return (
        <>
            <div className="space-y-4">
                {/* Ant Design Table with server-side pagination */}
                <Table
                    columns={columns}
                    dataSource={sortedResults}
                    rowKey={(record, index) => `${record.patientName}-${record.examDateTime}-${record.mrn}-${record.examType}-${index}`}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalCount,
                        onChange: (page, newPageSize) => {
                            setCurrentPage(page);
                            if (newPageSize !== pageSize) {
                                setPageSize(newPageSize);
                                setCurrentPage(1); // Reset to page 1 when page size changes
                            }
                        },
                        pageSizeOptions: ['25', '50', '100', '200'],
                        showSizeChanger: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} exams`,
                    }}
                    size="small"
                    scroll={{ y: 500 }}
                    virtual
                    loading={(isLoading && !isExpanded) || isFetching}
                />
            </div>

            {/* Patient Exam Pane */}
            {showAddExamPane && selectedPatient && (
                <PatientExamPane
                    patientAge={calculateAge(selectedPatient?.dob)}
                    patientInactive={false}
                    patient={selectedPatient}
                    showPane={showAddExamPane}
                    examInfo={selectedPatientExam}
                    examHistory={examResults}
                    facility={facility}
                    updateExamInfo={updateExamInfo}
                    from='unperformed-exams'
                    hidePane={() => setShowAddExamPane(false)}
                    sharedFollowUpIDs={sharedFollowUpIDs}
                    getNewExamInfo={getNewExamInfo}
                />
            )}

            <Modal
                title="Reason For Deletion"
                visible={showDeletionModal}
                onCancel={closeDeletionModal}
                onOk={handleConfirmDelete}
                okText="Delete"
                cancelText="Cancel"
            >
                <div className="row">
                    {examToDelete && (
                        <div className="form-group col-12 mb-3">
                            <div style={{ 
                                padding: '12px', 
                                backgroundColor: Globals.theme === 'dark' ? '#3f3f46' : '#fef2f2', 
                                borderRadius: '6px',
                                border: `1px solid ${Globals.theme === 'dark' ? '#71717a' : '#fecaca'}`
                            }}>
                                <strong>Patient:</strong> {examToDelete.patientName}<br />
                                <strong>MRN:</strong> {examToDelete.mrn}<br />
                                <strong>Exam Type:</strong> {examToDelete.examType}<br />
                                <strong>Scheduled Date:</strong> {formatDate(examToDelete.examDateTime)}
                            </div>
                        </div>
                    )}
                    
                    {examToDelete?.groupedExam && (
                        <div className="form-group col-12 mb-3">
                            <p>Would you also like to delete the grouped exam?: <strong><u>{groupedExamType || examToDelete.groupedExam}</u></strong>&nbsp;<span style={{ color: '#ec4899' }}>*</span></p>
                            <div style={{ marginLeft: '12px' }}>
                                <div className="custom-control custom-radio custom-control-inline">
                                    <input 
                                        type="radio" 
                                        name="deleteGroupedExam" 
                                        className="custom-control-input" 
                                        value="Yes" 
                                        checked={shouldDeleteGroupedExam === true} 
                                        onChange={() => setShouldDeleteGroupedExam(true)} 
                                    />
                                    <span className="custom-control-label">Yes</span>
                                </div>
                                <div className="custom-control custom-radio custom-control-inline">
                                    <input 
                                        type="radio" 
                                        name="deleteGroupedExam" 
                                        className="custom-control-input" 
                                        value="No" 
                                        checked={shouldDeleteGroupedExam === false} 
                                        onChange={() => setShouldDeleteGroupedExam(false)} 
                                    />
                                    <span className="custom-control-label">No</span>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    <div className="form-group col-12">
                        <label className="form-label">
                            Reason for Deletion <span style={{ color: '#ec4899' }}>*</span>
                        </label>
                        <select 
                            className="form-control-custom" 
                            name="deletionReason" 
                            value={deletionReason} 
                            onChange={(e) => setDeletionReason(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '6px',
                                border: `1px solid ${Globals.theme === 'dark' ? '#3f3f46' : '#e5e7eb'}`,
                                backgroundColor: Globals.theme === 'dark' ? '#18181b' : 'white',
                                color: Globals.theme === 'dark' ? '#e4e4e7' : '#18181b'
                            }}
                        >
                            <option value="">-- Select a reason --</option>
                            {deletionReasons.map((item, idx) => (
                                <option key={idx} value={item.reasonForDeletionID}>
                                    {item.reasonForDeletion}
                                </option>
                            ))}
                        </select>
                    </div>
                    
                    <div className="form-group col-12">
                        <em style={{ color: '#dc2626' }}>
                            <strong>NOTE:&nbsp;</strong>
                            Deleting this exam will permanently remove it from the system
                        </em>
                    </div>
                </div>
            </Modal>
        </>
    );
}
